import React, { createContext, PropsWithChildren, useContext, useRef } from 'react';
import { z, ZodError } from 'zod';
import { usePersistenceContext } from '../persistence-context/index';

type ValidationResult = {
	status?: 'error' | 'success';
	helperText?: string;
	onFocus: () => void;
};

export type ErrorContextProps = {
	/**
	 * Function to update the array of ids in the touchedRef to the 'touched' status (true)
	 * @param {string[]} fieldIds an array of field ids that all will be set to 'touched' upon navigating away from the current step
	 */
	setTouched: (fieldIds: string[]) => void;
	/**
	 * Function to validate one field Id. Checks if the field has been touched (focussed) and if the fieldId of the field exists in the Zod errors object
	 * @param {string} fieldId a singular fieldId gets passed in for validation
	 * @returns {ValidationResult} an object with an onFocus and an optional 'status' and 'helperText' properties. Status will either be 'error' or 'success'
	 */
	fieldProps: (fieldId: string) => ValidationResult;
	/**
	 * Function that looks at all fields to see if at least one exists in the error object
	 * @param {string[]} fieldId accepts one field id to validate
	 * @returns {boolean} a boolean to reflect whether at least one id is in the error object
	 */
	validateFields: (fieldId: string[]) => boolean;
};

/**
 * Custom Zod error map. Technically an empty field is considered a typeError (string, null).
 */

const customErrorMap: z.ZodErrorMap = (issue, ctx) => {
	if (issue.code === z.ZodIssueCode.invalid_type) {
	  if (issue.expected === "string") {
		return { message: "This field is required." };
	  }
	}
	return { message: ctx.defaultError };
  };
  
  z.setErrorMap(customErrorMap);

type ErrorValidator = { parse: (data: unknown) => void };
const Context = createContext<ErrorContextProps | undefined>(undefined);
Context.displayName = 'ErrorContext';

const validate = (data: unknown, schema: ErrorValidator): Record<string, string> => {
	const validationErrors: Record<string, string> = {};
	try {
		schema.parse(data);
	} catch (e) {
		const errors = e as ZodError;
		errors?.issues?.forEach(i => {
			i.path.forEach(p => {
				validationErrors[p?.toString()] = i.message;
			});
		});
	}
	return validationErrors;
};

export function ErrorContextProvider<T>({ children, schema }: PropsWithChildren<{ schema: ErrorValidator }>) {
	const { data: fetchData } = usePersistenceContext<T>();
	const data = fetchData();
	const touchedRef = useRef<Record<string, boolean>>({});
	let errors: Record<string, string> = {};
	if (data) {
		errors = validate(data, schema);
	}

	const setTouched = (fieldIds: string[]) => {
		fieldIds.forEach(f => (touchedRef.current[f] = true));
	};

	const validateFields = (fieldIds: string[]): boolean => {
		if (fieldIds.some(f => Object.keys(errors).some(e => e === f))) {
			return false;
		} else {
			return true;
		}
	};

	//TODO: Find a way to go to error state if field is focussed and then unfocussed with no change to the data
	const fieldProps = (fieldId: string): ValidationResult => {
		const base = { onFocus: () => setTouched([fieldId]) };
		if (errors[fieldId] && touchedRef.current[fieldId] === true) {
			return { ...base, status: 'error', helperText: errors[fieldId] };
		} else if (touchedRef.current[fieldId] === true) {
			return { ...base, status: 'success' };
		} 
		return base;
	};

	const contextValue = { setTouched, fieldProps, validateFields };

	return <Context.Provider value={contextValue}>{children}</Context.Provider>;
}

/**This is a context that is intended to be use with the PersistenceContext to validate user inputs. It uses a Zod validation schema to check for the absence of non-nullable values. */
export function useErrorContext() {
	const context = useContext(Context);

	return context as ErrorContextProps;
}
